import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Plus, Loader2, Edit2, Trash2, TrendingUp, TrendingDown,
  Calendar, FileText, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import OrcamentoForm from "../components/orcamento/OrcamentoForm";
import ComparativoOrcado from "../components/orcamento/ComparativoOrcado";
import RelatorioOrcamentario from "../components/orcamento/RelatorioOrcamentario";

export default function Orcamento() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const anoAtual = new Date().getFullYear();
  
  const [activeTab, setActiveTab] = useState("lista");
  const [anoSelecionado, setAnoSelecionado] = useState(anoAtual);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrcamento, setEditingOrcamento] = useState(null);

  const queryClient = useQueryClient();

  const { data: orcamentos = [], isLoading } = useQuery({
    queryKey: ['orcamentos', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Orcamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId }, '-data', 1000)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: custosAlocados = [] } = useQuery({
    queryKey: ['custos_alocados', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.CustoAlocado.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: centrosCusto = [] } = useQuery({
    queryKey: ['centros_custo', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.CentroCusto.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: propriedade } = useQuery({
    queryKey: ['propriedade', propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0])
      : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Orcamento.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orcamentos'] })
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const handleNew = () => {
    setEditingOrcamento(null);
    setDialogOpen(true);
  };

  const handleEdit = (orc) => {
    setEditingOrcamento(orc);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
    setDialogOpen(false);
    setEditingOrcamento(null);
  };

  // Filtrar por ano
  const orcamentosFiltrados = orcamentos.filter(o => o.ano === anoSelecionado);
  
  // Totais
  const totalReceitasOrcadas = orcamentosFiltrados.filter(o => o.tipo === 'receita').reduce((a, o) => a + (o.valor_total || 0), 0);
  const totalDespesasOrcadas = orcamentosFiltrados.filter(o => o.tipo === 'despesa').reduce((a, o) => a + (o.valor_total || 0), 0);

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Target className="w-8 h-8 text-indigo-600" />
            Orçamento Anual
          </h1>
          <p className="text-stone-600 mt-1">Planejamento e acompanhamento orçamentário</p>
        </div>
        
        <div className="flex gap-3">
          <Select value={anoSelecionado.toString()} onValueChange={(v) => setAnoSelecionado(parseInt(v))}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[anoAtual - 1, anoAtual, anoAtual + 1, anoAtual + 2].map(a => (
                <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={handleNew} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
            <Plus className="w-5 h-5" />
            Novo Orçamento
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Receitas Orçadas</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalReceitasOrcadas)}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Despesas Orçadas</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalDespesasOrcadas)}</p>
              </div>
              <TrendingDown className="w-10 h-10 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${totalReceitasOrcadas - totalDespesasOrcadas >= 0 ? 'bg-gradient-to-br from-indigo-500 to-indigo-600' : 'bg-gradient-to-br from-stone-500 to-stone-600'} text-white`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Resultado Orçado</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalReceitasOrcadas - totalDespesasOrcadas)}</p>
              </div>
              <Target className="w-10 h-10 text-white/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="lista" className="gap-2">
            <Target className="w-4 h-4" />
            Orçamentos
          </TabsTrigger>
          <TabsTrigger value="comparativo" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Comparativo
          </TabsTrigger>
          <TabsTrigger value="relatorio" className="gap-2">
            <FileText className="w-4 h-4" />
            Relatório
          </TabsTrigger>
        </TabsList>

        {/* Lista de Orçamentos */}
        <TabsContent value="lista" className="mt-6">
          {orcamentosFiltrados.length === 0 ? (
            <Card className="bg-white border-stone-200">
              <CardContent className="p-12 text-center">
                <Target className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-bold text-stone-800 mb-2">Nenhum orçamento para {anoSelecionado}</h3>
                <p className="text-stone-500 mb-4">Crie seu primeiro orçamento para começar o planejamento</p>
                <Button onClick={handleNew} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Orçamento
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {orcamentosFiltrados.map((orc, index) => {
                  const centro = centrosCusto.find(c => c.id === orc.centro_custo_id);
                  const safra = safras.find(s => s.id === orc.safra_id);
                  
                  return (
                    <motion.div
                      key={orc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className={`bg-white border-stone-200 hover:shadow-lg transition-all overflow-hidden ${
                        orc.tipo === 'receita' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'
                      }`}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-stone-800">{orc.nome}</h3>
                              <div className="flex flex-wrap gap-1 mt-1">
                                <Badge className={orc.tipo === 'receita' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {orc.tipo === 'receita' ? '📈 Receita' : '📉 Despesa'}
                                </Badge>
                                <Badge variant="outline" className="text-xs">{orc.categoria}</Badge>
                              </div>
                            </div>
                            {orc.tipo === 'receita' ? (
                              <TrendingUp className="w-5 h-5 text-green-500" />
                            ) : (
                              <TrendingDown className="w-5 h-5 text-red-500" />
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-stone-500">Valor Total</span>
                              <span className={`font-bold ${orc.tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>
                                {formatCurrency(orc.valor_total)}
                              </span>
                            </div>
                            
                            {(centro || safra) && (
                              <div className="flex flex-wrap gap-1">
                                {centro && <Badge variant="secondary" className="text-xs">🏢 {centro.nome}</Badge>}
                                {safra && <Badge variant="secondary" className="text-xs">🌾 {safra.nome}</Badge>}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 mt-4 pt-4 border-t border-stone-100">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleEdit(orc)}
                            >
                              <Edit2 className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => deleteMutation.mutate(orc.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>

        {/* Comparativo */}
        <TabsContent value="comparativo" className="mt-6">
          <ComparativoOrcado
            orcamentos={orcamentos}
            lancamentos={lancamentos}
            custosAlocados={custosAlocados}
            ano={anoSelecionado}
          />
        </TabsContent>

        {/* Relatório */}
        <TabsContent value="relatorio" className="mt-6">
          <RelatorioOrcamentario
            orcamentos={orcamentos}
            lancamentos={lancamentos}
            custosAlocados={custosAlocados}
            ano={anoSelecionado}
            propriedade={propriedade}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              {editingOrcamento ? "Editar Orçamento" : "Novo Orçamento"}
            </DialogTitle>
            <DialogDescription>
              Defina o orçamento anual por categoria e centro de custo
            </DialogDescription>
          </DialogHeader>
          <OrcamentoForm
            orcamento={editingOrcamento}
            centrosCusto={centrosCusto}
            safras={safras}
            onSuccess={handleSuccess}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
